import { useState } from 'react';
import { api } from '../lib/api.js';
import { Field } from './Field.jsx';
import { inputStyle } from '../lib/ui.js';

// Manager edit of a pending time-off request. CJ 2026-08-03: a request whose
// PTO hours exceeded what was left after an earlier approval could not be
// approved, and there was no way to adjust it — deny or nothing.
//
// The common repair is "drop the PTO hours to what's actually left, or take
// the rest unpaid", so the PTO fields are the ones that get the room here.
export function TORManagerEditModal({ tor, currentUser, onClose, onDone }) {
  const [form, setForm] = useState({
    requested_date_start: tor.requested_date_start,
    requested_date_end: tor.requested_date_end,
    full_day: !tor.requested_time_start && !tor.requested_time_end,
    requested_time_start: tor.requested_time_start || '09:00',
    requested_time_end: tor.requested_time_end || '17:00',
    use_pto: !!Number(tor.use_pto),
    pto_hours: tor.pto_hours == null ? '' : String(tor.pto_hours),
    reason: tor.reason || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const badRange = form.requested_date_start > form.requested_date_end;
  const badPto = form.use_pto && !(Number(form.pto_hours) > 0);
  const blocked = badRange || badPto;

  async function save() {
    if (blocked) {
      setErr(badRange ? 'End date must be on or after the start date.'
                      : 'PTO hours must be greater than zero.');
      return;
    }
    setBusy(true); setErr('');
    try {
      await api('PUT', `/api/tor/${tor.id}`, {
        requested_date_start: form.requested_date_start,
        requested_date_end: form.requested_date_end,
        requested_time_start: form.full_day ? null : form.requested_time_start,
        requested_time_end: form.full_day ? null : form.requested_time_end,
        use_pto: form.use_pto,
        pto_hours: form.use_pto ? Number(form.pto_hours) : null,
        reason: form.reason || null,
      }, { 'x-user-id': String(currentUser.id) });
      onDone();
    } catch (e) {
      // The server returns the balance breakdown on a PTO rejection; show
      // the message it sent rather than a generic failure, because that
      // message is the one that says how many hours are actually left.
      let msg = e.message || 'Could not save the changes';
      try { const p = JSON.parse(msg); msg = p.error || msg; } catch { /* plain string */ }
      setErr(msg);
    } finally { setBusy(false); }
  }

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 480, width: '100%',
                  maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Edit time off request</div>
        <div style={{ fontSize: 13, color: '#37474f', marginBottom: 14 }}>
          <strong>{tor.employee_name}</strong>
          {tor.employee_role ? ` · ${tor.employee_role}` : ''}
          <div style={{ fontSize: 11, color: '#757575', marginTop: 2 }}>
            They’ll be notified that you changed it. It stays pending either way.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="First day">
            <input type="date" value={form.requested_date_start}
              onChange={(e) => setForm((f) => ({ ...f, requested_date_start: e.target.value }))}
              style={inputStyle} />
          </Field>
          <Field label="Last day">
            <input type="date" value={form.requested_date_end}
              onChange={(e) => setForm((f) => ({ ...f, requested_date_end: e.target.value }))}
              style={inputStyle} />
          </Field>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, margin: '10px 0' }}>
          <input type="checkbox" checked={form.full_day}
            onChange={(e) => setForm((f) => ({ ...f, full_day: e.target.checked }))} />
          All day
        </label>
        {!form.full_day && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Field label="From">
              <input type="time" value={form.requested_time_start}
                onChange={(e) => setForm((f) => ({ ...f, requested_time_start: e.target.value }))}
                style={inputStyle} />
            </Field>
            <Field label="To">
              <input type="time" value={form.requested_time_end}
                onChange={(e) => setForm((f) => ({ ...f, requested_time_end: e.target.value }))}
                style={inputStyle} />
            </Field>
          </div>
        )}

        <div style={{ borderTop: '1px solid #eee', margin: '12px 0', paddingTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={form.use_pto}
              onChange={(e) => setForm((f) => ({ ...f, use_pto: e.target.checked }))} />
            Use PTO (paid)
          </label>
          {form.use_pto && (
            <div style={{ marginTop: 8 }}>
              <Field label="PTO hours">
                <input type="number" min="0" step="0.5" value={form.pto_hours}
                  onChange={(e) => setForm((f) => ({ ...f, pto_hours: e.target.value }))}
                  style={inputStyle} />
              </Field>
              <div style={{ fontSize: 11, color: '#757575', marginTop: 4 }}>
                Checked against what this person has left, excluding this request.
                If it’s more than they have, the save is refused and tells you the number.
              </div>
            </div>
          )}
          {!form.use_pto && Number(tor.use_pto) === 1 && (
            <div style={{ fontSize: 11, color: '#a16207', marginTop: 6 }}>
              Turning PTO off makes this unpaid time off. Nothing is deducted from their balance.
            </div>
          )}
        </div>

        <Field label="Reason">
          <input value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            style={inputStyle} />
        </Field>

        {err && <div style={{ fontSize: 12, color: '#c62828', marginTop: 8 }}>{err}</div>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
          <button onClick={save} disabled={busy || blocked}
            style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 16px', fontSize: 13, fontWeight: 600,
                      cursor: (busy || blocked) ? 'not-allowed' : 'pointer',
                      opacity: (busy || blocked) ? 0.5 : 1 }}>
            {busy ? '…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
